import _ from "lodash";
import { action, autorun, computed, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { Message } from "primereact/message";
import * as React from "react";
import styled from "styled-components";
import { getLocStore } from "../localization";
import { LocContext } from "../localization/LocContext";
import { translateErrorMessageFromBackend } from "../localization/translateErrorMessageFromBackend";
import { WSErrorContainerContext } from "./WSErrorContainerContext";
import { routerStore } from "../url/WSRouter";
import { lp } from "../utils/logPrefix";
import { wsSentryReport } from "./wsSentryReport";

@observer
export class WSErrorContainer extends React.Component<
  {
    showDialogOnCrash?: boolean;
    onComponentDidCatch?: (error: any, info: any) => any;
    onDismiss?: () => any;
  },
  { hasError: boolean }
> {
  render() {
    if (this.state.hasError) {
      const url =
        window.logUrl +
        `/search?q=CvpBffCorrelationId%3A+${this.correlationId}` +
        `&rangetype=relative&relative=0`;

      const link = (
        <a href={url} target="_blank">
          {this.correlationId}
        </a>
      );

      return (
        <StyledMessage
          className="ws-error-message"
          severity="error"
          content={
            <div
              onClick={() =>
                this.reportError({
                  forceShowDialog: true,
                  alreadyReported: true,
                })
              }
            >
              {!!this.isLoadingChunkError && (
                <div style={{ fontWeight: "bold" }}>
                  {getLocStore(LocContext.GENERAL)
                    .id("c2366e204e86")
                    .loc(
                      "[Loading chunk error] Denne typen feil kan av og til rettes med en hard-refresh. (Ctrl+f5 eller CMD+R)"
                    )}
                </div>
              )}
              <div>{this.errorMessage}</div>
              <div>
                CorrelationId: {window.isLocalIp ? link : this.correlationId}
              </div>
            </div>
          }
        />
      );
    }

    return (
      <WSErrorContainerContext.Provider value={this}>
        {this.props.children}
      </WSErrorContainerContext.Provider>
    );
  }

  disposers = [];

  componentWillUnmount() {
    this.disposers.forEach((d) => d());
    this.disposers = [];
  }

  componentDidMount() {
    this.disposers.push(
      autorun(() => {
        routerStore.location;
        this.setState({ hasError: false });
      })
    );
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  @action addAdditionalErrorMessageInfo = (info: {
    key: string;
    errorMessage: string;
    [x: string]: any;
  }) => {
    _.remove(
      this.additionalErrorMessageInfo,
      (_info) => _info.key === info.key
    );

    this.additionalErrorMessageInfo.push(info);
  };

  @observable additionalErrorMessageInfo: {
    key: string;
    errorMessage: string;
    [x: string]: any;
  }[] = [];

  @computed get onDismiss() {
    return this.props.onDismiss;
  }

  @computed get isLoadingChunkError() {
    try {
      const msg = ((this.error || {}).message + "").toLowerCase();

      return msg.startsWith("loading chunk") || msg.includes(".chunk.js");
    } catch (e) {
      return false;
    }
  }

  static defaultProps = {
    showDialogOnCrash: false,
  };

  @computed get errorMessage() {
    const fallback = getLocStore(LocContext.GENERAL)
      .id("e9622b6a7a6a")
      .loc("Noe gikk galt");

    const translatedMessage = translateErrorMessageFromBackend(
      this.error?.message
    );

    const statusCode = this.error?.statusCode;

    if (statusCode) {
      return `[${statusCode}] - ${translatedMessage ?? fallback}`;
    }

    return translatedMessage ?? fallback;
  }

  @observable error = null;
  @observable info = null;
  @observable correlationId: string = null;

  @action setError(error, info) {
    runInAction(() => {
      this.error = error;
      this.info = info;
      if (error.cvpBffCorrelationId) {
        this.correlationId = error.cvpBffCorrelationId;
      }
    });

    console.error(lp(), info);

    if (this.props.onComponentDidCatch) {
      this.props.onComponentDidCatch(error, info);
    }

    this.reportError({});
  }

  componentDidCatch(error, info) {
    this.setError(error, info);
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  @action reportError = ({
    forceShowDialog = false,
    alreadyReported = false,
  }) => {
    this.correlationId = wsSentryReport({
      simpleMessage: this.errorMessage,
      correlationId: this.correlationId,
      error: this.error,
      additionalErrorMessageInfo: this.additionalErrorMessageInfo,
      info: this.info,
      tag: "WSErrorContainer",
      showDialog: this.props.showDialogOnCrash || forceShowDialog,
      alreadyReported,
    });
  };
}

const StyledMessage = styled(Message)`
  &.ws-error-message {
    cursor: pointer;
    white-space: pre-wrap;
  }
`;
